import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area,
  ComposedChart,
  ResponsiveContainer
} from 'recharts';

// Mock data for rapid prototyping
const matterStatusData = [
  { name: 'Active', value: 42, color: '#10B981' },
  { name: 'On Hold', value: 8, color: '#F59E0B' },
  { name: 'Completed', value: 23, color: '#3B82F6' },
  { name: 'Cancelled', value: 3, color: '#EF4444' }
];

const billingTrendData = [
  { month: 'Jan', amount: 45000 },
  { month: 'Feb', amount: 52000 },
  { month: 'Mar', amount: 48000 },
  { month: 'Apr', amount: 61000 },
  { month: 'May', amount: 55000 },
  { month: 'Jun', amount: 67000 }
];

const clientActivityData = [
  { month: 'Jan', matters: 12, documents: 234 },
  { month: 'Feb', matters: 15, documents: 189 },
  { month: 'Mar', matters: 8, documents: 278 },
  { month: 'Apr', matters: 18, documents: 321 },
  { month: 'May', matters: 11, documents: 156 },
  { month: 'Jun', matters: 14, documents: 298 }
];

// Attorney Productivity Data - User-specific
const attorneyUtilizationData = [
  { month: 'Jan', billableHours: 142, targetHours: 160, utilization: 89, revenue: 35500 },
  { month: 'Feb', billableHours: 156, targetHours: 160, utilization: 98, revenue: 39000 },
  { month: 'Mar', billableHours: 134, targetHours: 160, utilization: 84, revenue: 33500 },
  { month: 'Apr', billableHours: 168, targetHours: 160, utilization: 105, revenue: 42000 },
  { month: 'May', billableHours: 145, targetHours: 160, utilization: 91, revenue: 36250 },
  { month: 'Jun', billableHours: 172, targetHours: 160, utilization: 108, revenue: 43000 }
];

const currentQuarterData = [
  { 
    week: 'Week 1', 
    targetHours: 40, 
    revenue: 8500,
    contractReview: 12,
    litigation: 14,
    corporate: 6,
    compliance: 4
  },
  { 
    week: 'Week 2', 
    targetHours: 40, 
    revenue: 9200,
    contractReview: 14,
    litigation: 11,
    corporate: 8,
    compliance: 5
  },
  { 
    week: 'Week 3', 
    targetHours: 40, 
    revenue: 9800,
    contractReview: 16,
    litigation: 13,
    corporate: 7,
    compliance: 3
  },
  { 
    week: 'Week 4', 
    targetHours: 40, 
    revenue: 9500,
    contractReview: 15,
    litigation: 12,
    corporate: 8,
    compliance: 3
  },
  { 
    week: 'Week 5', 
    targetHours: 40, 
    revenue: 10500,
    contractReview: 16,
    litigation: 14,
    corporate: 7,
    compliance: 5
  },
  { 
    week: 'Week 6', 
    targetHours: 40, 
    revenue: 8750,
    contractReview: 12,
    litigation: 10,
    corporate: 8,
    compliance: 5
  },
  { 
    week: 'Week 7', 
    targetHours: 40, 
    revenue: 11250,
    contractReview: 18,
    litigation: 15,
    corporate: 9,
    compliance: 3
  },
  { 
    week: 'Week 8', 
    targetHours: 40, 
    revenue: 10250,
    contractReview: 14,
    litigation: 13,
    corporate: 8,
    compliance: 6
  }
];

const matterDistributionData = [
  { client: 'TechCorp Inc.', hours: 45, revenue: 11250, color: '#3B82F6' },
  { client: 'Global Industries', hours: 38, revenue: 9500, color: '#10B981' },
  { client: 'StartupXYZ', hours: 22, revenue: 5500, color: '#F59E0B' },
  { client: 'Enterprise LLC', hours: 31, revenue: 7750, color: '#8B5CF6' },
  { client: 'Others', hours: 14, revenue: 3500, color: '#6B7280' }
];

// Chart Components for Hot-Swapping

export const MatterStatusPieChart: React.FC = () => (
  <ResponsiveContainer width="100%" height={300}>
    <PieChart>
      <Pie
        data={matterStatusData}
        cx="50%"
        cy="50%"
        labelLine={false}
        label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
        outerRadius={80}
        fill="#8884d8"
        dataKey="value"
      >
        {matterStatusData.map((entry, index) => (
          <Cell key={`cell-${index}`} fill={entry.color} />
        ))}
      </Pie>
      <Tooltip />
    </PieChart>
  </ResponsiveContainer>
);

export const BillingTrendChart: React.FC = () => (
  <ResponsiveContainer width="100%" height={300}>
    <AreaChart data={billingTrendData}>
      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
      <XAxis dataKey="month" stroke="#9CA3AF" />
      <YAxis stroke="#9CA3AF" />
      <Tooltip 
        contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151' }}
        labelStyle={{ color: '#F3F4F6' }}
      />
      <Area 
        type="monotone" 
        dataKey="amount" 
        stroke="#3B82F6" 
        fill="#3B82F6" 
        fillOpacity={0.3}
      />
    </AreaChart>
  </ResponsiveContainer>
);

export const ClientActivityChart: React.FC = () => (
  <ResponsiveContainer width="100%" height={300}>
    <LineChart data={clientActivityData}>
      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
      <XAxis dataKey="month" stroke="#9CA3AF" />
      <YAxis stroke="#9CA3AF" />
      <Tooltip 
        contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151' }}
        labelStyle={{ color: '#F3F4F6' }}
      />
      <Legend />
      <Line type="monotone" dataKey="matters" stroke="#10B981" strokeWidth={2} />
      <Line type="monotone" dataKey="documents" stroke="#F59E0B" strokeWidth={2} />
    </LineChart>
  </ResponsiveContainer>
);

export const MatterVolumeBarChart: React.FC = () => (
  <ResponsiveContainer width="100%" height={300}>
    <BarChart data={billingTrendData}>
      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
      <XAxis dataKey="month" stroke="#9CA3AF" />
      <YAxis stroke="#9CA3AF" />
      <Tooltip 
        contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151' }}
        labelStyle={{ color: '#F3F4F6' }}
      />
      <Bar dataKey="amount" fill="#8B5CF6" />
    </BarChart>
  </ResponsiveContainer>
);

// Attorney Productivity Charts

export const AttorneyUtilizationChart: React.FC = () => (
  <ResponsiveContainer width="100%" height={300}>
    <LineChart data={attorneyUtilizationData}>
      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
      <XAxis dataKey="month" stroke="#9CA3AF" />
      <YAxis stroke="#9CA3AF" />
      <Tooltip 
        contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151' }}
        labelStyle={{ color: '#F3F4F6' }}
        formatter={(value, name) => [
          name === 'utilization' ? `${value}%` : value,
          name === 'billableHours' ? 'Billable Hours' : 
          name === 'targetHours' ? 'Target Hours' : 'Utilization %'
        ]}
      />
      <Legend />
      <Line type="monotone" dataKey="billableHours" stroke="#10B981" strokeWidth={3} name="Billable Hours" />
      <Line type="monotone" dataKey="targetHours" stroke="#6B7280" strokeWidth={2} strokeDasharray="5 5" name="Target Hours" />
      <Line type="monotone" dataKey="utilization" stroke="#F59E0B" strokeWidth={2} name="Utilization %" />
    </LineChart>
  </ResponsiveContainer>
);

export const AttorneyRevenueChart: React.FC = () => (
  <ResponsiveContainer width="100%" height={300}>
    <AreaChart data={attorneyUtilizationData}>
      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
      <XAxis dataKey="month" stroke="#9CA3AF" />
      <YAxis stroke="#9CA3AF" />
      <Tooltip 
        contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151' }}
        labelStyle={{ color: '#F3F4F6' }}
        formatter={(value) => [`$${value.toLocaleString()}`, 'Revenue']}
      />
      <Area 
        type="monotone" 
        dataKey="revenue" 
        stroke="#059669" 
        fill="#059669" 
        fillOpacity={0.4}
      />
    </AreaChart>
  </ResponsiveContainer>
);

export const WeeklyProductivityChart: React.FC = () => (
  <ResponsiveContainer width="100%" height={350}>
    <ComposedChart data={currentQuarterData}>
      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
      <XAxis dataKey="week" stroke="#9CA3AF" />
      <YAxis stroke="#9CA3AF" />
      <Tooltip 
        contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151' }}
        labelStyle={{ color: '#F3F4F6' }}
        formatter={(value, name) => {
          if (name === 'targetHours') {
            return [`${value} hours`, 'Target Hours'];
          }
          // Format matter type names for display
          const displayName = typeof name === 'string' 
            ? name.charAt(0).toUpperCase() + name.slice(1).replace(/([A-Z])/g, ' $1')
            : String(name);
          return [`${value} hours`, displayName];
        }}
      />
      <Legend />
      {/* Stacked bars for matter types */}
      <Bar dataKey="contractReview" stackId="matters" fill="#3B82F6" name="Contract Review" />
      <Bar dataKey="litigation" stackId="matters" fill="#10B981" name="Litigation" />
      <Bar dataKey="corporate" stackId="matters" fill="#F59E0B" name="Corporate" />
      <Bar dataKey="compliance" stackId="matters" fill="#8B5CF6" name="Compliance" />
      {/* Target line */}
      <Line 
        type="monotone" 
        dataKey="targetHours" 
        stroke="#EF4444" 
        strokeWidth={3} 
        strokeDasharray="5 5"
        name="Target Hours"
        dot={{ fill: '#EF4444', strokeWidth: 2, r: 4 }}
      />
    </ComposedChart>
  </ResponsiveContainer>
);

export const ClientMatterDistributionChart: React.FC = () => (
  <ResponsiveContainer width="100%" height={300}>
    <PieChart>
      <Pie
        data={matterDistributionData}
        cx="50%"
        cy="50%"
        labelLine={false}
        label={({ client, hours }) => `${client}: ${hours}h`}
        outerRadius={100}
        fill="#8884d8"
        dataKey="hours"
      >
        {matterDistributionData.map((entry, index) => (
          <Cell key={`cell-${index}`} fill={entry.color} />
        ))}
      </Pie>
      <Tooltip 
        contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151' }}
        labelStyle={{ color: '#F3F4F6' }}
        formatter={(value, name) => [
          name === 'hours' ? `${value} hours` : `$${value.toLocaleString()}`,
          name === 'hours' ? 'Billable Hours' : 'Revenue'
        ]}
      />
    </PieChart>
  </ResponsiveContainer>
);

// Widget Container Component
interface ChartWidgetProps {
  title: string;
  children: React.ReactNode;
  className?: string;
}

export const ChartWidget: React.FC<ChartWidgetProps> = ({ title, children, className = '' }) => (
  <div className={`bg-gray-800 rounded-lg border border-gray-700 p-6 ${className}`}>
    <h3 className="text-lg font-semibold text-white mb-4">{title}</h3>
    <div className="text-gray-300">
      {children}
    </div>
  </div>
);

// Chart Configuration for Dynamic Loading
export const chartComponents = {
  matterStatus: MatterStatusPieChart,
  billingTrend: BillingTrendChart,
  clientActivity: ClientActivityChart,
  matterVolume: MatterVolumeBarChart,
  attorneyUtilization: AttorneyUtilizationChart,
  attorneyRevenue: AttorneyRevenueChart,
  weeklyProductivity: WeeklyProductivityChart,
  clientDistribution: ClientMatterDistributionChart,
};

export const chartTitles = {
  matterStatus: 'Matter Status Distribution',
  billingTrend: 'Billing Trend (6 Months)',
  clientActivity: 'Client Activity Overview',
  matterVolume: 'Matter Volume by Month',
  attorneyUtilization: 'Attorney Utilization vs Targets',
  attorneyRevenue: 'Attorney Revenue Trend',
  weeklyProductivity: 'My Weekly Productivity (Trailing 8 Weeks)',
  clientDistribution: 'Client Matter Distribution',
};

export const chartDescriptions = {
  matterStatus: 'Current distribution of matter statuses across all active cases',
  billingTrend: 'Monthly billing amounts and revenue trends over the past 6 months',
  clientActivity: 'Overview of client engagement and matter activity levels',
  matterVolume: 'Number of new matters opened each month',
  attorneyUtilization: 'Individual attorney billable hours vs monthly targets with utilization percentage',
  attorneyRevenue: 'Revenue generated by attorney over time showing productivity impact',
  weeklyProductivity: 'Weekly billable hours breakdown by matter type vs targets for trailing 8 weeks',
  clientDistribution: 'Distribution of billable hours across different clients and matters',
};

// Chart Categories for Organization
export const chartCategories = {
  general: ['matterStatus', 'billingTrend', 'clientActivity', 'matterVolume'],
  attorney: ['attorneyUtilization', 'attorneyRevenue', 'weeklyProductivity', 'clientDistribution'],
};
